import { useEffect, useState } from "react";
import { ACTIVE_WEBINAR_PROMO, WebinarPromoEvent } from "./webinarPromo.config";

/**
 * Polled "now" for the webinar promo surfaces. Re-renders every 30s so the
 * LIVE badge, the "Join meeting" CTA, and the countdown flip near the
 * minute boundary even when the user has been sitting on the dashboard.
 * 30s is a compromise — small enough that no user notices a stale state,
 * large enough that it's effectively free.
 *
 * Preview override:
 * When `event.previewNow` is a valid ISO 8601 timestamp the hook returns
 * that fixed moment instead and skips polling — so testers can sit in any
 * state (countdown / join window / LIVE / ended) indefinitely. Leave the
 * field unset/commented in production. See `webinarPromo.config.ts`.
 *
 * Co-located with the other webinar promo files (kept out of the pure
 * `webinarPromo.config.ts` so that file stays free of React imports).
 */
export function useWebinarPromoNow(
  event: WebinarPromoEvent = ACTIVE_WEBINAR_PROMO
): Date {
  const previewMs = event.previewNow ? new Date(event.previewNow).getTime() : NaN;
  const hasValidPreview = !Number.isNaN(previewMs);

  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    // Don't poll when a preview override is in effect — keep "now" frozen
    // so the tester can stay in the previewed state indefinitely.
    if (hasValidPreview) return;
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, [hasValidPreview]);

  return hasValidPreview ? new Date(previewMs) : now;
}
